import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Check, ShieldCheck, UserRound, KeyRound, MapPin, ClipboardCheck } from "lucide-react";
import { Button, Checkbox, PageHero, PhoneInput } from "../../components";
import EmailCodeStep from "../../components/EmailCodeStep/EmailCodeStep";
import CustomerAddressField from "../../components/CustomerAddressField/CustomerAddressField";
import { CaptchaWidget } from "../../components/CaptchaWidget/CaptchaWidget";
import { useAuth } from "../../context/AuthContext";
import { REGISTER_EMAIL_CODE_URL, REGISTER_URL, ORDER_CONFIG_URL } from "../../features/UrlHelper";
import "./RegistraciaPage.scss";

/**
 * Registracia zakaznika prevadzky (R1, R9, R23, R41).
 *
 * PRECO SAMOSTATNA STRANKA A NIE MODAL:
 *   Prihlasenie ostava modalom, ale registracia obsahuje mapu s adresami --
 *   do modalu sa to na mobile rozumne nezmesti a mapa v modali nad modalom
 *   je zle ovladatelna jednou rukou.
 *
 * ADRESA JE NEPOVINNA:
 *   Mapa hned pri zakladani uctu ludi odradzala, tak sa da preskocit. Kto ju
 *   vyplni, ma objednavanie hotove na pár klikov; kto nie, doplni si ju
 *   v profile a vypyta si ju az odoslanie objednavky. Server to ma rovnako
 *   (customerAuthController -- `nepovinne: true`).
 *
 * DVA KROKY:
 *   1. udaje + adresy + suhlas + captcha  -> posle sa kod na e-mail
 *   2. kod z e-mailu                      -> ucet vznikne a clovek je prihlaseny
 *   Medzi krokmi sa na serveri nic neuklada, takze nedokoncena registracia
 *   nenechava polovicne ucty. Preto sa v kroku 2 posielaju udaje znova.
 */
const PRAZDNY = {
  email: "",
  password: "",
  passwordConfirm: "",
  name: "",
  surname: "",
};

const MIN_HESLO = 8;

function RegistraciaPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [krok, setKrok] = useState("form");
  const [udaje, setUdaje] = useState(PRAZDNY);
  const [telefon, setTelefon] = useState("");
  const [predvolba, setPredvolba] = useState("+421");
  const [adresy, setAdresy] = useState([]);
  const [suhlas, setSuhlas] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);

  const [listok, setListok] = useState("");
  const [chyba, setChyba] = useState("");
  const [info, setInfo] = useState("");
  const [odosiela, setOdosiela] = useState(false);
  const [zapnute, setZapnute] = useState(null);

  // Prevadzka, ktora online objednavky nema zapnute, nema ani registraciu --
  // ucet by nemal na co sluzit. Server to odmietne tiez, toto je len to,
  // aby sa clovek zbytocne nevypisoval.
  useEffect(() => {
    let zrusene = false;
    axios.get(ORDER_CONFIG_URL)
      .then(({ data }) => { if (!zrusene) setZapnute(data?.zapnute === true); })
      .catch(() => { if (!zrusene) setZapnute(false); });
    return () => { zrusene = true; };
  }, []);

  const zmen = (pole) => (e) => setUdaje((s) => ({ ...s, [pole]: e.target.value }));

  const resetCaptchy = () => {
    captchaRef.current?.reset?.();
    setCaptcha(null);
  };

  /** Kontroly, ktore vie spravit prehliadac. Server ich robi znova. */
  function chybaFormulara() {
    if (!udaje.email.trim()) return "Zadajte e-mail.";
    if (!udaje.name.trim() || !udaje.surname.trim()) return "Vyplňte meno aj priezvisko.";
    if (udaje.password.length < MIN_HESLO) return `Heslo musí mať aspoň ${MIN_HESLO} znakov.`;
    if (udaje.password !== udaje.passwordConfirm) return "Heslá sa nezhodujú.";
    if (!telefon.trim()) return "Zadajte telefónne číslo.";
    if (!suhlas) return "Na registráciu je potrebný súhlas s podmienkami.";
    if (!captcha) return "Počkajte na overenie, že nie ste robot.";
    return null;
  }

  async function posliKod(e) {
    e?.preventDefault?.();
    const chybaVstupu = chybaFormulara();
    if (chybaVstupu) { setChyba(chybaVstupu); return; }

    setOdosiela(true);
    setChyba("");
    try {
      const { data } = await axios.post(REGISTER_EMAIL_CODE_URL, {
        email: udaje.email.trim().toLowerCase(),
        cfTurnstileToken: captcha,
      });
      setListok(data.listok);
      setKrok("kod");
      setInfo("");
    } catch (err) {
      setChyba(err?.response?.data?.message || "Kód sa nepodarilo odoslať. Skúste to znova.");
      resetCaptchy();
    } finally {
      setOdosiela(false);
    }
  }

  /** Znovuposlanie potrebuje novu captchu -- tá predošlá je jednorazová. */
  async function posliKodZnova() {
    setInfo("");
    setChyba("");
    if (!captcha) {
      setChyba("Obnovte, prosím, overenie proti robotom nižšie a skúste to znova.");
      setKrok("form");
      return;
    }
    setOdosiela(true);
    try {
      const { data } = await axios.post(REGISTER_EMAIL_CODE_URL, {
        email: udaje.email.trim().toLowerCase(),
        cfTurnstileToken: captcha,
        predoslyListok: listok,
      });
      setListok(data.listok);
      setInfo("Poslali sme nový kód.");
    } catch (err) {
      setChyba(err?.response?.data?.message || "Kód sa nepodarilo odoslať.");
      resetCaptchy();
      setKrok("form");
    } finally {
      setOdosiela(false);
    }
  }

  async function dokonci(kod) {
    setOdosiela(true);
    setChyba("");
    try {
      const { data } = await axios.post(REGISTER_URL, {
        email: udaje.email.trim().toLowerCase(),
        password: udaje.password,
        passwordConfirm: udaje.passwordConfirm,
        name: udaje.name.trim(),
        surname: udaje.surname.trim(),
        phone_number: `${predvolba}${telefon}`,
        addresses: adresy,
        agreeTerms: true,
        emailListok: listok,
        emailKod: kod,
      });
      login(data);
      navigate("/objednavka", { replace: true });
    } catch (err) {
      const sprava = err?.response?.data?.message || "Registráciu sa nepodarilo dokončiť.";
      setChyba(sprava);
      // Obsadený e-mail alebo neplatné údaje sa opravujú vo formulári,
      // nie v kroku s kódom -- inak by tam človek ostal visieť.
      if (err?.response?.status === 409 || err?.response?.status === 403) {
        setKrok("form");
        resetCaptchy();
      }
    } finally {
      setOdosiela(false);
    }
  }

  // Stav jednotlivých krokov -- svieti v hlavičke karty aj v súhrne vpravo,
  // takže človek pred odoslaním vidí, čo mu ešte chýba.
  const hesloNesedi = udaje.passwordConfirm !== "" && udaje.password !== udaje.passwordConfirm;
  const kroky = [
    {
      id: "udaje",
      nazov: "Kontaktné údaje",
      hotovo: Boolean(udaje.name.trim() && udaje.surname.trim() && telefon.trim()),
    },
    {
      id: "prihlasenie",
      nazov: "Prihlasovacie údaje",
      hotovo: Boolean(
        udaje.email.includes("@")
        && udaje.password.length >= MIN_HESLO
        && udaje.password === udaje.passwordConfirm
      ),
    },
    {
      id: "adresa",
      nazov: "Adresa doručenia",
      hotovo: adresy.length > 0,
      nepovinne: true,
    },
  ];
  const hotovo = (id) => kroky.find((k) => k.id === id).hotovo;

  // Prihlásenie je modal v hlavičke; `?prihlasenie` ho otvorí (Header.jsx)
  // a človek zostane na tejto stránke.
  const hero = (
    <PageHero
      eyebrow="Nový účet"
      title="Vytvorte si"
      zvyraznene="účet"
      lead={
        <>
          Stačí pár údajov a obed si potom objednáte na niekoľko klikov.
          Už účet máte?{" "}
          <Link to={{ search: "?prihlasenie" }} className="registracia__odkaz">Prihláste sa</Link>
        </>
      }
    />
  );

  if (zapnute === false) {
    return (
      <main className="registracia registracia--off">
        <h1>Online objednávky zatiaľ nie sú spustené</h1>
        <p>Jedlo si u nás objednáte telefonicky.</p>
        <Link to="/kontakt" className="registracia__odkaz">Kontakt a čas objednávok</Link>
      </main>
    );
  }

  if (krok === "kod") {
    return (
      <main className="registracia">
        {hero}
        <div className="registracia__obsah">
          <div className="registracia__karta">
            <EmailCodeStep
              email={udaje.email}
              onSubmit={dokonci}
              onResend={posliKodZnova}
              onBack={() => { setKrok("form"); setChyba(""); setInfo(""); }}
              backLabel="Upraviť údaje"
              submitLabel="Dokončiť registráciu"
              chyba={chyba}
              info={info}
              odosiela={odosiela}
            />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="registracia">
      {hero}

      {/*
        Karty aj súhrn sú v jednej mriežke v poradí, v akom sa vypĺňajú.
        Na desktope: hore údaje a prihlasovanie vedľa seba, pod nimi adresy
        cez celú šírku a pod adresami súhrn s tlačidlom -- odoslanie je vždy
        až za posledným krokom, nie nad ním. Pod desktopom všetko pod sebou.
      */}
      <form className="registracia__obsah registracia__layout" onSubmit={posliKod} noValidate>
        <KartaKroku cislo={1} ikona={UserRound} nazov="Vaše údaje" hotovo={hotovo("udaje")}>
          <label className="registracia__pole">
            <span className="registracia__label">Meno</span>
            <input className="registracia__input" value={udaje.name} onChange={zmen("name")} autoComplete="given-name" required />
          </label>
          <label className="registracia__pole">
            <span className="registracia__label">Priezvisko</span>
            <input className="registracia__input" value={udaje.surname} onChange={zmen("surname")} autoComplete="family-name" required />
          </label>
          <div className="registracia__pole">
            <label className="registracia__label" htmlFor="registracia-telefon">Telefón</label>
            <PhoneInput
              id="registracia-telefon"
              value={telefon}
              prefix={predvolba}
              onChange={setTelefon}
              onPrefixChange={setPredvolba}
              required
            />
            <small className="registracia__hint">Potrebuje ho vodič, keby vás nenašiel.</small>
          </div>
        </KartaKroku>

        <KartaKroku cislo={2} ikona={KeyRound} nazov="Prihlasovanie" hotovo={hotovo("prihlasenie")}>
          <label className="registracia__pole">
            <span className="registracia__label">E-mail</span>
            <input
              className="registracia__input"
              type="email"
              value={udaje.email}
              onChange={zmen("email")}
              autoComplete="email"
              placeholder="meno@example.com"
              required
            />
            <small className="registracia__hint">Bude to zároveň vaše prihlasovacie meno.</small>
          </label>
          <label className="registracia__pole">
            <span className="registracia__label">Heslo</span>
            <input
              className="registracia__input"
              type="password"
              value={udaje.password}
              onChange={zmen("password")}
              autoComplete="new-password"
              minLength={MIN_HESLO}
              required
            />
            <small className="registracia__hint">Aspoň {MIN_HESLO} znakov.</small>
          </label>
          <label className="registracia__pole">
            <span className="registracia__label">Heslo znova</span>
            <input
              className={`registracia__input ${hesloNesedi ? "registracia__input--chyba" : ""}`.trim()}
              type="password"
              value={udaje.passwordConfirm}
              onChange={zmen("passwordConfirm")}
              autoComplete="new-password"
              aria-invalid={hesloNesedi || undefined}
              required
            />
            {hesloNesedi && (
              <small className="registracia__hint registracia__hint--chyba">Heslá sa nezhodujú.</small>
            )}
          </label>
        </KartaKroku>

        <KartaKroku
          cislo={3}
          ikona={MapPin}
          nazov="Vaše adresy pre rozvoz"
          hotovo={hotovo("adresa")}
          nepovinne
          popis="Adresu vyhľadajte a potvrďte jej polohu na mape. Pokojne ju preskočte — doplniť si ju môžete kedykoľvek v profile, stačí to stihnúť pred prvou objednávkou."
          siroka
        >
          <CustomerAddressField adresy={adresy} onChange={setAdresy} disabled={odosiela} />
        </KartaKroku>

        <aside className="registracia__suhrn">
          <h2 className="registracia__suhrn-nadpis">
            <ClipboardCheck size={18} aria-hidden="true" /> Dokončenie
          </h2>

          {/* Na desktope tri stĺpce: čo chýba | súhlas | odoslanie. */}
          <div className="registracia__suhrn-telo">
            <ul className="registracia__kontrola">
              {kroky.map((k) => (
                <li key={k.id} className={k.hotovo ? "registracia__kontrola--hotovo" : ""}>
                  <span className="registracia__kontrola-znak" aria-hidden="true">
                    {k.hotovo && <Check size={12} strokeWidth={3} />}
                  </span>
                  {k.nazov}
                  {/* Nevyplnena nepovinna polozka nesmie vyzerat ako chyba --
                      inak clovek hlada, co mu brani odoslat. */}
                  {k.nepovinne && !k.hotovo && (
                    <em className="registracia__kontrola-nepovinne">nepovinné</em>
                  )}
                  <span className="registracia__skryte">
                    {k.hotovo ? " – vyplnené" : k.nepovinne ? " – nepovinné" : " – chýba"}
                  </span>
                </li>
              ))}
            </ul>

            <div className="registracia__suhrn-suhlas">
              <Checkbox
                checked={suhlas}
                onChange={setSuhlas}
                /*
                 * Znenie je zámerné: údaje sa spracúvajú na základe ZMLUVY a zákonnej
                 * povinnosti, nie súhlasu. „Súhlas so spracovaním“ by právny základ
                 * zahmlil a navádzal by na predstavu, že sa dá odvolať — pritom bez
                 * tých údajov objednávku vybaviť nemožno. Súhlasí sa s podmienkami,
                 * zásady sa berú na vedomie.
                 */
                label={
                  <>
                    Súhlasím s{" "}
                    <Link to="/vop" target="_blank" rel="noopener noreferrer">obchodnými podmienkami</Link>
                    {" "}a beriem na vedomie{" "}
                    <Link to="/ochrana-osobnych-udajov" target="_blank" rel="noopener noreferrer">
                      zásady ochrany osobných údajov
                    </Link>.
                  </>
                }
              />
              <p className="registracia__poznamka">
                <ShieldCheck size={15} aria-hidden="true" />
                Na e-mail vám pošleme overovací kód. Účet vznikne až po jeho zadaní.
              </p>
            </div>

            <div className="registracia__suhrn-odoslanie">
              <CaptchaWidget onVerify={setCaptcha} turnstileRef={captchaRef} />

              {chyba && <p className="registracia__chyba" role="alert">{chyba}</p>}

              <Button type="submit" variant="primary" className="registracia__odoslat" disabled={odosiela}>
                {odosiela ? "Odosielam..." : "Pokračovať"}
              </Button>
            </div>
          </div>
        </aside>
      </form>
    </main>
  );
}

/**
 * Karta jedného kroku: číslo, ktoré sa po vyplnení zmení na fajku, nadpis
 * a polia. Číslovanie dáva dlhému formuláru poradie bez toho, aby ho bolo
 * treba deliť na viac obrazoviek.
 */
function KartaKroku({ cislo, ikona: Ikona, nazov, popis, hotovo, nepovinne = false, siroka = false, children }) {
  return (
    <section className={`registracia__krok ${siroka ? "registracia__krok--siroky" : ""}`.trim()}>
      <header className="registracia__krok-hlavicka">
        <span
          className={`registracia__krok-cislo ${hotovo ? "registracia__krok-cislo--hotovo" : ""}`.trim()}
          aria-hidden="true"
        >
          {hotovo ? <Check size={15} strokeWidth={3} /> : cislo}
        </span>
        <h2 className="registracia__krok-nadpis">
          <Ikona size={17} aria-hidden="true" />
          {nazov}
          {nepovinne && <span className="registracia__krok-nepovinne">nepovinné</span>}
        </h2>
      </header>
      <div className="registracia__krok-telo">
        {popis && <p className="registracia__krok-popis">{popis}</p>}
        {children}
      </div>
    </section>
  );
}

export default RegistraciaPage;
